import React, { useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { editorStateToHtml, handlePastedHtml } from "./optionRichTextHtml";

export interface OptionRichTextProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const hexToRgb = (hex: string): string => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgb(${r},${g},${b})`;
};

type ToolbarComponentProps = {
  onChange: (key: string, value: string) => void;
  currentState?: { color?: string };
};

const CustomColorPicker: React.FC<ToolbarComponentProps> = ({
  onChange,
  currentState,
}) => {
  const handlePick = (hex: string) => {
    onChange("color", hexToRgb(hex));
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0 4px",
        height: 30,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <label
        title="Color del texto"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          cursor: "pointer",
          fontSize: 12,
          color: "#555",
        }}
      >
        <span
          style={{
            fontWeight: 700,
            color: currentState?.color || "#000",
            borderBottom: `3px solid ${currentState?.color || "#000"}`,
            lineHeight: 1,
            padding: "0 2px",
          }}
        >
          A
        </span>
        <input
          type="color"
          onChange={(e) => handlePick(e.target.value)}
          style={{
            width: 22,
            height: 22,
            border: "1px solid var(--bo-border-strong)",
            borderRadius: 4,
            padding: 0,
            cursor: "pointer",
            background: "transparent",
          }}
        />
      </label>
    </div>
  );
};

const TOOLBAR_CONFIG = {
  options: ["inline", "fontSize", "colorPicker"],
  inline: {
    options: ["bold"],
  },
  fontSize: {
    options: [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72],
  },
  colorPicker: {
    component: CustomColorPicker,
  },
};

function htmlToEditorState(html: string): EditorState {
  if (!html) return EditorState.createEmpty();
  const blocks = htmlToDraft(html);
  if (!blocks) return EditorState.createEmpty();
  const content = ContentState.createFromBlockArray(
    blocks.contentBlocks,
    blocks.entityMap,
  );
  return EditorState.createWithContent(content);
}

const OptionRichText: React.FC<OptionRichTextProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const lastEmittedHtmlRef = useRef<string>("");
  const [editorState, setEditorState] = useState<EditorState>(() =>
    htmlToEditorState(value || ""),
  );

  useEffect(() => {
    if (value === lastEmittedHtmlRef.current) return;
    setEditorState(htmlToEditorState(value || ""));
  }, [value]);

  const handleChange = (next: EditorState) => {
    setEditorState(next);
    const html = editorStateToHtml(next);
    lastEmittedHtmlRef.current = html;
    onChange(html);
  };

  return (
    <div
      style={{
        border: "1px solid var(--bo-border-strong)",
        borderRadius: 6,
        background: "var(--bo-card-bg)",
        flex: 1,
        minWidth: 280,
      }}
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={handleChange}
        // SCRUM-1115: lo pegado entra sin color / fondo / tamaño / fuente
        // heredados (ver optionRichTextHtml.ts).
        handlePastedText={(_text, html, state, onChange) =>
          handlePastedHtml(html, state, onChange)
        }
        toolbar={TOOLBAR_CONFIG}
        placeholder={placeholder}
        localization={{ locale: "es" }}
        toolbarStyle={{
          border: "none",
          borderBottom: "1px solid var(--bo-card-border)",
          marginBottom: 0,
          padding: 4,
        }}
        editorStyle={{
          padding: "4px 10px",
          minHeight: 40,
        }}
      />
    </div>
  );
};

export default OptionRichText;
